import type { ReactNode } from 'react';

import { useState } from 'react';
import { Footer, Header } from './Shell.js';
import { Signup } from './Signup.js';

/**
 * Landing do produto.
 *
 * Estrutura e copy vieram do mockup aprovado. A unica liberdade tomada foi
 * ligar os botoes ao portal e a documentacao real — em vez de ancoras vazias.
 */
export function App(): ReactNode {
  // Plano escolhido abre o formulário; null mantém fechado.
  const [planoEscolhido, setPlanoEscolhido] = useState<string | null>(null);

  return (
    <>
      <Header />

      <main>
        <div className="wrap hero">
          <div>
            <h1>Emita NF-e, NFC-e e NFS-e com uma chamada de API.</h1>
            <p className="lead">
              O FA Tech Notas recebe o JSON do seu ERP ou PDV, calcula os tributos, assina o XML e conversa
              com a SEFAZ e a Receita por você. Você recebe a nota autorizada por webhook.
            </p>
            <div className="cta">
              <a className="btn btn-primary" href="#planos">
                Começar no sandbox
              </a>
              <a className="btn btn-ghost" href="#api">
                Ver a API
              </a>
            </div>
            <div className="facts">
              <div>
                <b className="num">3</b>
                <span>modelos fiscais em uma só API</span>
              </div>
              <div>
                <b className="num">5 anos</b>
                <span>de guarda legal dos XMLs</span>
              </div>
              <div>
                <b className="num">0</b>
                <span>SAT, MFE ou hardware no caixa</span>
              </div>
            </div>
          </div>

          <aside className="receipt" aria-label="Exemplo de emissão de NFC-e">
            <div className="head">
              <b>NFC-e nº 12.341</b>
              <span>Série 1 · Loja Centro</span>
            </div>
            <ol>
              <li className="done">
                <span className="dot" />
                <span>Pedido recebido do PDV</span>
                <time>14:22:10</time>
              </li>
              <li className="done">
                <span className="dot" />
                <span>Tributos calculados · XML assinado</span>
                <time>14:22:10</time>
              </li>
              <li className="done">
                <span className="dot" />
                <span>Transmitida à SEFAZ MS</span>
                <time>14:22:11</time>
              </li>
              <li className="now">
                <span className="dot" />
                <span>Autorizada · protocolo 150260000012345</span>
                <time>14:22:12</time>
              </li>
            </ol>
            <div className="total">
              <span>R$ 49,80</span>
              <span className="stamp">AUTORIZADA</span>
            </div>
            <p className="chave key">5026 0962 1037 8000 0152 6500 1000 0012 3410 0001 2349</p>
          </aside>
        </div>

        <section id="como">
          <div className="wrap">
            <div className="section-head">
              <h2>Do pedido à nota autorizada em quatro passos.</h2>
              <p>Você cuida da venda. O resto é sequência automática.</p>
            </div>
            <div className="steps">
              {STEPS.map((step) => (
                <div key={step.n}>
                  <small>{step.n}</small>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Faixa com fundo um degrau acima do resto: separa o bloco sem
            precisar de mais uma borda. A cor vem do token, nao de um `style`
            inline — era um `#fff` cravado aqui que sobreviveu a virada para
            o tema escuro e deixou a secao ilegivel. */}
        <section id="recursos" className="band">
          <div className="wrap">
            <div className="section-head">
              <h2>Feito para o Brasil de 2026.</h2>
              <p>Reforma tributária, NFS-e nacional e varejo sem hardware — já resolvidos.</p>
            </div>
            <div className="feat">
              {FEATURES.map((f) => (
                <article key={f.title}>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="api">
          <div className="wrap">
            <div className="code-band">
              <div>
                <h2>Uma requisição. Qualquer linguagem.</h2>
                <p>
                  API REST com JSON, chaves de idempotência e webhooks assinados. SDKs oficiais e sandbox
                  conectado à homologação da SEFAZ.
                </p>
                <div className="langs">
                  {['Node.js', 'PHP', 'C#', 'Python', 'Postman'].map((l) => (
                    <span key={l}>{l}</span>
                  ))}
                </div>
              </div>
              <pre>
                <span className="c">POST /v1/documents</span>
                {'\n{\n  '}
                <span className="k">"emitter_id"</span>: <span className="s">"emt_01J8XK"</span>,{'\n  '}
                <span className="k">"model"</span>: <span className="s">"nfce"</span>,{'\n  '}
                <span className="k">"reference"</span>: <span className="s">"PDV-0042-000123"</span>,{'\n  '}
                <span className="k">"items"</span>: [{'{'}
                {'\n    '}
                <span className="k">"description"</span>: <span className="s">"Arroz 5kg"</span>,{'\n    '}
                <span className="k">"ncm"</span>: <span className="s">"10063021"</span>,{' '}
                <span className="k">"cfop"</span>: <span className="s">"5102"</span>,{'\n    '}
                <span className="k">"quantity"</span>: 2, <span className="k">"unit_price"</span>: 24.90
                {'\n  }],\n  '}
                <span className="k">"payments"</span>: [{'{ '}
                <span className="k">"method"</span>: <span className="s">"credit_card"</span>,{' '}
                <span className="k">"amount"</span>: 49.80 {'}]\n}'}
                {'\n\n'}
                <span className="c">→ 202 Accepted</span>
                {'\n{ '}
                <span className="k">"id"</span>: <span className="s">"doc_01J8XM"</span>,{' '}
                <span className="k">"status"</span>: <span className="s">"queued"</span> {'}'}
              </pre>
            </div>
          </div>
        </section>

        <section id="planos">
          <div className="wrap">
            <div className="section-head">
              <h2>Planos que crescem com a operação.</h2>
              <p>Comece grátis no sandbox. Pague só pelo que autorizar.</p>
            </div>
            <div className="plans">
              {PLANS.map((plan) => (
                <div key={plan.name} className={`plan${plan.featured ? ' featured' : ''}`}>
                  <h3>{plan.name}</h3>
                  {/* O preço por nota é o número que o cliente compara entre
                      fornecedores; a mensalidade vem logo abaixo, menor. */}
                  <div className={`price${plan.porNota ? ' num' : ''}`}>
                    {plan.porNota ?? 'Sob consulta'}
                    {plan.porNota && <small>por NF emitida</small>}
                  </div>
                  <div className="mensalidade">{plan.mensalidade}</div>
                  <div className="per">{plan.limits}</div>
                  <ul>
                    {plan.features.map((f) => (
                      <li key={f}>{f}</li>
                    ))}
                  </ul>
                  {plan.id ? (
                    <button
                      className={`btn ${plan.featured ? 'btn-primary' : 'btn-ghost'}`}
                      onClick={() => setPlanoEscolhido(plan.id)}
                    >
                      {plan.cta}
                    </button>
                  ) : (
                    // Enterprise é negociado: o caminho é falar com a gente,
                    // não um cadastro que não sabe o preço.
                    <a className="btn btn-ghost" href="mailto:comercial@fatechbrasil.com.br">
                      {plan.cta}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Signup plano={planoEscolhido} onFechar={() => setPlanoEscolhido(null)} />

      <Footer />
    </>
  );
}

const STEPS = [
  {
    n: '1',
    title: 'Envie o JSON',
    body: 'Um único formato para os três modelos. Sem XML, sem schema, sem SOAP do seu lado.',
  },
  {
    n: '2',
    title: 'Calculamos e assinamos',
    body: 'ICMS, ISS, PIS/COFINS e as novas tags de IBS e CBS. Assinatura com o certificado A1 da sua empresa, guardado em cofre.',
  },
  {
    n: '3',
    title: 'Falamos com o governo',
    body: 'SEFAZ estaduais para NF-e e NFC-e; Ambiente de Dados Nacional para NFS-e. Se cair, entramos em contingência sozinhos.',
  },
  {
    n: '4',
    title: 'Você recebe o retorno',
    body: 'Webhook com a nota autorizada, o PDF pronto e, se houver rejeição, uma explicação que qualquer pessoa entende.',
  },
];

const FEATURES = [
  {
    title: 'Tributação em transição',
    body: 'Cada item sai com os impostos atuais e com IBS e CBS lado a lado, conforme as notas técnicas vigentes. As tabelas são atualizadas por nós.',
  },
  {
    title: 'NFS-e padrão nacional',
    body: 'Uma integração para todos os municípios aderentes. Enviamos a DPS direto para a Receita Federal.',
  },
  {
    title: 'Contingência automática',
    body: 'SEFAZ fora do ar? A NFC-e é assinada e impressa na hora com QR Code de contingência, e transmitida sozinha quando o serviço voltar.',
  },
  {
    title: 'Rejeições traduzidas',
    body: '"Rejeição 778" vira "O NCM do item 1 não existe. Corrija o cadastro do produto." Com o campo exato a ajustar.',
  },
  {
    title: 'Certificado em cofre',
    body: 'O .pfx é criptografado na entrada e nunca sai do cofre. Avisamos 30, 15 e 5 dias antes de vencer.',
  },
  {
    title: 'Guarda legal de 5 anos',
    body: 'XMLs e PDFs em armazenamento imutável, com exportação mensal para o contador.',
  },
];

const PLANS = [
  {
    id: 'easy',
    name: 'Easy',
    // R$ 49,90 / 100 notas. O excedente é a própria mensalidade dividida
    // pela franquia: quem passa do plano paga o mesmo por nota, sem degrau.
    porNota: 'R$ 0,50',
    mensalidade: 'R$ 49,90/mês · 100 notas inclusas',
    limits: '1 CNPJ · NF-e, NFC-e e NFS-e',
    features: ['API e portal', 'Webhooks', 'Guarda legal de 5 anos', 'Suporte por e-mail'],
    cta: 'Começar',
    featured: false,
  },
  {
    id: 'starter',
    name: 'Starter',
    porNota: 'R$ 0,30',
    mensalidade: 'R$ 149/mês · 500 notas inclusas',
    limits: '1 CNPJ · NF-e, NFC-e e NFS-e',
    features: [
      'Tudo do Easy',
      'Contingência offline da NFC-e',
      'Exportação para o contador',
      'Suporte por e-mail',
    ],
    cta: 'Começar',
    featured: false,
  },
  {
    id: 'business',
    name: 'Business',
    porNota: 'R$ 0,09',
    mensalidade: 'R$ 449/mês · 5.000 notas inclusas',
    limits: '5 CNPJs · NF-e, NFC-e e NFS-e',
    features: [
      'Tudo do Starter',
      'Sandbox dedicado',
      'Disponibilidade de 99,5%',
      'Suporte em horário comercial',
    ],
    cta: 'Começar',
    featured: true,
  },
  {
    id: null,
    name: 'Enterprise',
    porNota: null,
    mensalidade: 'Volume e preço negociados',
    limits: 'CNPJs e volume ilimitados',
    features: [
      'Tudo do Business',
      'IPs fixos para allowlist',
      'Disponibilidade de 99,9%',
      'Suporte prioritário e gerente de conta',
    ],
    cta: 'Falar com o comercial',
    featured: false,
  },
];
